import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// In-memory response cache for fast, 0-delay tab switching
const apiCache = new Map();
const CACHE_TTL_MS = 45000; // 45 seconds

export function clearApiCache() {
  apiCache.clear();
}

api.clearCache = clearApiCache;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rw_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Any mutation (POST, PATCH, PUT, DELETE) clears cache to ensure data freshness
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") {
    clearApiCache();
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    const method = (response.config?.method || "get").toLowerCase();
    if (method === "get" && !response.config?.skipCache) {
      const token = localStorage.getItem("rw_token") || "";
      const url = response.config?.url || "";
      const params = JSON.stringify(response.config?.params || {});
      const cacheKey = `${token}::${url}::${params}`;
      apiCache.set(cacheKey, {
        timestamp: Date.now(),
        data: response.data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 401) {
      // If unauthorized and not already on login, clear token
      if (window.location.pathname !== "/login" && window.location.pathname !== "/register") {
        localStorage.removeItem("rw_token");
        clearApiCache();
      }
    }
    return Promise.reject(error);
  }
);

// Wrap api.get to serve cached responses instantly
const originalGet = api.get.bind(api);
api.get = function (url, config = {}) {
  if (!config.skipCache) {
    const token = localStorage.getItem("rw_token") || "";
    const params = JSON.stringify(config.params || {});
    const cacheKey = `${token}::${url}::${params}`;
    const cached = apiCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return Promise.resolve({
        data: cached.data,
        status: cached.status,
        statusText: cached.statusText,
        headers: cached.headers,
        config,
      });
    }
  }
  return originalGet(url, config);
};

export function formatApiError(detail) {
  // Return null so callers can fall back to the transport error message
  // (e.g. "Network Error", timeouts, 404, etc.).
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
